import React, { useCallback, useMemo, useContext, useEffect } from 'react';
import { TableContext } from '../shared/table/context/TableContext';
import BaseTable from '../shared/table/BaseTable';
import { useColumns } from './hooks/useColumns';
import TransactionListToolbar from './TransactionListToolbar';
import TransactionCard from './TransactionCard';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const TransactionListTable = ({
  transactions = [],
  type = 'sales',
  onTransactionClick
}) => {
  const { setColumns, filterRows, sortRows, applyFilters } = useContext(TableContext);
  const columns = useColumns(transactions, type);

  // Update columns in context when they change
  useEffect(() => {
    setColumns(columns);
  }, [columns, setColumns]);

  // Process transactions for display
  const processedTransactions = useMemo(() => {
    const filtered = sortRows(applyFilters(filterRows(transactions)));
    return filtered.map(transaction => ({
      ...transaction,
      // Add any computed fields here
      coins_description: transaction.coin_count > 1 
        ? `Multiple Coins (${transaction.coin_count})` 
        : transaction.coins_description
    }));
  }, [transactions, filterRows, sortRows, applyFilters]);

  const handleRowClick = useCallback((id, button) => {
    // Both left and middle clicks show modal since transactions are viewed in a modal
    if (onTransactionClick) {
      onTransactionClick(id);
    }
  }, [onTransactionClick]);

  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <div className="flex flex-col h-full">
      <div className="flex-none">
        <TransactionListToolbar
          title={type === 'sales' ? 'Sales' : 'Purchases'}
          columns={columns}
          data={transactions}
          showQuickFilter={true}
          quickFilterProps={{ debounceMs: 300 }}
        />
      </div>
      <div className="flex-1 min-h-0">
        {isMobile ? (
          <div className="p-4 space-y-4 bg-gray-50">
            {processedTransactions.map(transaction => (
              <TransactionCard
                key={transaction.id}
                transaction={transaction}
                onTransactionClick={onTransactionClick}
                type={type}
              />
            ))}
          </div>
        ) : (
          <BaseTable
            data={processedTransactions}
            columns={columns}
            onRowClick={handleRowClick}
            showThumbnails={false}
          />
        )}
      </div>
    </div>
  );
};

export default TransactionListTable;
